import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from 'react-query'

import {
  IconPauseFill,
  IconPlayOutline,
  IconSettingsOutline
} from 'assets/svg/sprite'
import { RadioButton } from 'components-ref/atoms'
import { useGlobalVideo } from 'context/globalVideoContext'
import VideoSurface from 'context/VideoSurface'
import { useAutoplay, useVideoPlayedTracking } from 'helpers/hooks'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'

import './_videoPlayer.scss'
import ReplayGraphSVG from './ReplayGraph'
import VideoPlayerControls from './VideoPlayerControls/VideoPlayerControls'
import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'

interface Props {
  url: string
  showControls?: boolean
  minimized: boolean
  toggleControls?: () => void
  setShowControls?: (showControls: boolean) => void
  isActive?: boolean
  onPlay?: () => void
  shouldTrackPlayed?: boolean
  videoId?: string
  videoUserId?: string
  encoded?: any
  poster?: any
  cloudfront?: CloudFrontCredentials | null
  duration?: number
  onPause?: () => void
  onEnded?: () => void
  segment_data?: number[]
  controlsDisabled?: boolean
  pauseWhenPreviewClosed?: boolean
  ownerType?: 'post' | 'preview' | 'message'
  className?: string
}

const ALLOWED_HEIGHTS = [2160, 1440, 1080, 720, 480, 360, 240]

const VideoPlayer: FC<Props> = ({
  url,
  showControls,
  minimized,
  isActive = false,
  onPlay: onPlayStart,
  shouldTrackPlayed,
  videoId,
  videoUserId,
  poster,
  cloudfront,
  duration,
  onPause,
  onEnded,
  toggleControls,
  segment_data,
  controlsDisabled = false,
  pauseWhenPreviewClosed = true,
  ownerType = 'post',
  className
}) => {
  const userData = useUserProfile()
  const previewOpen = usePreviewStore(state => state.previewOpen)
  const queryClient = useQueryClient()
  const { autoplay } = useAutoplay({ onMutate: () => {} })

  const {
    play,
    pause,
    seekTo,
    setMuted: setGlobalMuted,
    setVolume: setGlobalVolume,
    setPlaybackRate,
    setQualityLevel,
    getVideoElement,
    getHls,
    getSelectedQualityLevel,
    getCurrentMedia
  } = useGlobalVideo()

  const mediaId = String(videoId ?? url)
  const isDisabledPlaceholder = controlsDisabled && !url

  // Normal Preview uses previewOpen. Independent fullscreen viewers don't.
  const surfaceActive =
    isActive &&
    (!pauseWhenPreviewClosed || previewOpen) &&
    !isDisabledPlaceholder

  const [playing, setPlaying] = useState(false)
  const [manuallyToggled, setManuallyToggled] = useState(false)
  const [volumeBarShowing, setVolumeBarShowing] = useState(false)
  const [volume, setVolume] = useState(100)
  const [muted, setMuted] = useState(false)
  const [videoDuration, setVideoDuration] = useState(
    typeof duration === 'number' && duration > 0 ? duration : 0
  )
  const [progressData, setProgressData] = useState({
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    loadedSeconds: 0
  })
  const [showReplayGraph, setShowReplayGraph] = useState(false)
  const commentsActive = usePreviewStore(state => state.commentsActive)
  const [levels, setLevels] = useState<
    { height: number; actualIndex: number }[]
  >([])
  const [settingsShowing, setSettingsShowing] = useState(false)
  const [showSpeed, setShowSpeed] = useState(false)
  const [playbackRate, setPlaybackRateState] = useState(1)

  const uniqueIdRef = useRef(
    crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  )

  const getMediaVideoElement = useCallback(
    () => getVideoElement(mediaId, url),
    [getVideoElement, mediaId, url]
  )

  const resolutionMenuRef = useRef<HTMLDivElement | null>(null)
  const speedMenuRef = useRef<HTMLDivElement | null>(null)
  const commentsWasActiveRef = useRef(commentsActive)
  const sourceUrl = url ?? ''

  const isBlob = useMemo(
    () => sourceUrl.startsWith('blob:') || sourceUrl.startsWith('data:video'),
    [sourceUrl]
  )
  const { updateMaxPlayedS, sendPlayedOnEnded } = useVideoPlayedTracking(
    shouldTrackPlayed,
    url,
    videoId,
    videoUserId,
    videoDuration
  )

  useEffect(() => {
    if (typeof duration === 'number' && duration > 0) {
      setVideoDuration(duration)
    }
  }, [duration])

  // Only the active surface may modify the shared native video.
  useEffect(() => {
    if (!surfaceActive) return
    setGlobalMuted(muted)
  }, [surfaceActive, muted, setGlobalMuted])

  useEffect(() => {
    if (!surfaceActive) return
    setGlobalVolume(volume)
  }, [surfaceActive, volume, setGlobalVolume])

  // Keep legacy/non-global players stoppable, but inactive shared-player
  // components must never pause the single global video.
  useEffect(() => {
    const subscription = emitter.addListener(
      EventsEmitter.STOP_ALL_VIDEOS,
      payload => {
        if (!surfaceActive) return
        if (payload?.instanceId === uniqueIdRef.current) return

        setPlaying(false)
        pause()
      }
    )

    return () => subscription.remove()
  }, [surfaceActive, pause])

  // Fullscreen behavior: active video plays when its surface becomes active.
useEffect(() => {
  if (!surfaceActive) return

  const video = getVideoElement(mediaId, url)

  if (video) {
    setPlaying(!video.paused && !video.ended)
  }

  if (!commentsActive) {
    void play(mediaId, url)
  }
}, [
  surfaceActive,
  mediaId,
  url,
  commentsActive,
  getVideoElement,
  play
])

// RESUME PLAY AFTER CLOSING COMMENTS
useEffect(() => {
  const wasActive = commentsWasActiveRef.current
  commentsWasActiveRef.current = commentsActive

  if (!wasActive || commentsActive || !surfaceActive) return

  void play(mediaId, url)
}, [commentsActive, surfaceActive, play])

useEffect(() => {
  if (surfaceActive || !playing) return

  setPlaying(false)

  if (
    pauseWhenPreviewClosed &&
    !previewOpen &&
    getCurrentMedia()?.id === mediaId &&
    getCurrentMedia()?.url.split('?')[0] === url.split('?')[0]
  ) {
    pause()
  }
}, [
  surfaceActive,
  playing,
  previewOpen,
  pauseWhenPreviewClosed,
  pause
])

  useEffect(() => {
    if (surfaceActive) {
      setManuallyToggled(false)
    }
  }, [surfaceActive, mediaId])

  // Only the currently active VideoPlayer listens to the shared element.
  useEffect(() => {
    if (!surfaceActive) return

    const video = getMediaVideoElement()
    if (!video) return

    const updateLevels = () => {
      const hls = getHls()

      if (!hls) {
        setLevels([])
        return
      }

      const seen = new Set<number>()

      setLevels(
        hls.levels
          .map((level, index) => ({
            height: level.height,
            actualIndex: index
          }))
          .filter(level => {
            if (
              !ALLOWED_HEIGHTS.includes(level.height) ||
              seen.has(level.height)
            ) {
              return false
            }

            seen.add(level.height)
            return true
          })
          .sort((a, b) => b.height - a.height)
      )
    }

    const syncProgress = () => {
      const d = video.duration

      if (!Number.isFinite(d) || d <= 0) return

      const playedSeconds = video.currentTime
      const played = Math.max(0, Math.min(1, playedSeconds / d))

      let loadedSeconds = 0
      let loaded = 0

      for (let i = 0; i < video.buffered.length; i++) {
        const start = video.buffered.start(i)
        const end = video.buffered.end(i)

        if (playedSeconds >= start && playedSeconds <= end) {
          loadedSeconds = end
          loaded = Math.max(0, Math.min(1, end / d))
          break
        }
      }

      setProgressData({
        played,
        playedSeconds,
        loaded,
        loadedSeconds
      })
    }

    const handleLoadedMetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        setVideoDuration(video.duration)
      }

      syncProgress()
      updateLevels()
    }

    const handleTimeUpdate = () => {
      syncProgress()

      if (
        !video.duration ||
        !Number.isFinite(video.duration)
      ) {
        return
      }

      const playedSeconds = video.currentTime

      if (!isBlob && shouldTrackPlayed) {
        updateMaxPlayedS(playedSeconds)
      }

    }

    const handlePlay = () => {
      setPlaying(true)

      emitter.emit(EventsEmitter.STOP_ALL_VIDEOS, {
        instanceId: uniqueIdRef.current
      })

      onPlayStart?.()
    }

    const handlePause = () => {
      setPlaying(false)
      onPause?.()
    }

    const handleEnded = () => {
      setPlaying(false)
      syncProgress()

      onEnded?.()
      sendPlayedOnEnded()
    }

    // Important for PostVideo -> fullscreen transfers:
    // moving an already-playing <video> does not emit a new play event.
    setPlaying(!video.paused && !video.ended)

    if (Number.isFinite(video.duration) && video.duration > 0) {
      setVideoDuration(video.duration)
      syncProgress()
    }

    updateLevels()

    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('durationchange', handleLoadedMetadata)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('progress', syncProgress)
    video.addEventListener('seeked', syncProgress)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('durationchange', handleLoadedMetadata)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('progress', syncProgress)
      video.removeEventListener('seeked', syncProgress)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
  }, [
    surfaceActive,
    mediaId,
    getVideoElement,
    getHls,
    videoDuration,
    videoId,
    videoUserId,
    userData?.id,
    shouldTrackPlayed,
    isBlob,
    updateMaxPlayedS,
    sendPlayedOnEnded,
    onPlayStart,
    onPause,
    onEnded
  ])

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node

      if (
        settingsShowing &&
        resolutionMenuRef.current &&
        !resolutionMenuRef.current.contains(target)
      ) {
        setSettingsShowing(false)
      }

      if (
        showSpeed &&
        speedMenuRef.current &&
        !speedMenuRef.current.contains(target)
      ) {
        setShowSpeed(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [settingsShowing, showSpeed])


  const playVideo = () => {
    const video = getMediaVideoElement()

    if (
      video &&
      (
        video.ended ||
        (
          Number.isFinite(video.duration) &&
          video.duration > 0 &&
          video.currentTime >= video.duration - 0.1
        )
      )
    ) {
      seekTo(0, 'seconds')
    }

    setPlaying(true)
    void play()
  }

  const pauseVideo = () => {
    setPlaying(false)
    pause()
  }

  const handleOnSeekChange = (fraction: number) => {
    seekTo(fraction, 'fraction')
  }

  const handleAutoplay = (prevState = false) => {
    if (isBlob) return

    autoplay.mutate(
      { prevState } as any,
      {
        onSuccess: () => {
          queryClient.invalidateQueries('user-profile')
        }
      }
    )
  }

  const handleSpeed = (rate: number) => {
    setPlaybackRate(rate)
    setPlaybackRateState(rate)
    setShowSpeed(false)
  }

  const handleQuality = (level: number) => {
    setQualityLevel(level)
    setSettingsShowing(false)
  }

  return (
    <div
      className={`videoPlayer videoPlayer--${minimized ? 'minimized' : ''} ${
        controlsDisabled ? 'videoPlayer--controlsDisabled' : ''
      }`}
      onClick={() => {
        if (controlsDisabled) return

        setManuallyToggled(true)

        if (playing) pauseVideo()
        else playVideo()

        toggleControls?.()
      }}
    >
      {!minimized && !controlsDisabled && manuallyToggled && (
        <div
          className={`videoPlayer__icon videoPlayer__icon${
            playing ? '--playing' : '--notPlaying'
          }`}
          onClick={event => {
            event.stopPropagation()
            setManuallyToggled(true)

            if (playing) pauseVideo()
            else playVideo()
          }}
        >
          {playing ? (
            <IconPauseFill />
          ) : (
            <IconPlayOutline color='#fff' />
          )}
        </div>
      )}

      {isDisabledPlaceholder ? (
        poster ? (
          <img
            src={poster}
            alt=""
            className={`videoPlayer__posterPlaceholder ${
              className ?? 'preview__file__video'
            }`}
            draggable={false}
          />
        ) : null
      ) : (
        <VideoSurface
          ownerType={ownerType}
          ownerId={mediaId}
          mediaId={mediaId}
          videoId={videoId}
          videoUserId={videoUserId}
          url={url}
          poster={poster}
          cloudfront={cloudfront}
          active={surfaceActive}
          pauseAndStopLoadingOnDetach={pauseWhenPreviewClosed}
          className={className ?? 'preview__file__video'}
        />
      )}

      {showControls && !controlsDisabled && (
        <>
          <div
            className='videoPlayerControlsResulation'
            ref={resolutionMenuRef}
            onClick={event => event.stopPropagation()}
          >
            <div
              onClick={event => {
                event.stopPropagation()
                setSettingsShowing(previous => !previous)
                setShowSpeed(false)
              }}
            >
              <IconSettingsOutline />
            </div>

            {settingsShowing && (
              <div
                className='videoPlayerControlsResulationSelect'
                onClick={event => event.stopPropagation()}
              >
                <div
                  className='videoPlayerControls__setting'
                  onClick={() => handleQuality(-1)}
                >
                  <RadioButton
                    active={getSelectedQualityLevel() === -1}
                  />

                  <div className='videoPlayerControls__setting--name'>
                    Auto
                  </div>
                </div>

                {levels.map(level => (
                  <div
                    key={level.height}
                    className='videoPlayerControls__setting'
                    onClick={() => handleQuality(level.actualIndex)}
                  >
                    <RadioButton
                      active={
                        getSelectedQualityLevel() ===
                        level.actualIndex
                      }
                    />

                    <div className='videoPlayerControls__setting--name'>
                      {level.height}p
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='videoControls'>
            <div
              ref={speedMenuRef}
              className='custom-select-wrapper'
              onClick={event => event.stopPropagation()}
            >
              <div
                className='selected-value'
                onClick={event => {
                  event.stopPropagation()
                  setShowSpeed(previous => !previous)
                  setSettingsShowing(false)
                }}
              >
                {playbackRate}x
              </div>

              {showSpeed && (
                <div className='videoPlayerControls__setting no-arrow'>
                  {[0.5, 1, 1.25, 1.5, 1.75, 2].map(rate => (
                    <div
                      key={rate}
                      className={`custom-option ${
                        playbackRate === rate ? 'active' : ''
                      }`}
                      onClick={event => {
                        event.stopPropagation()
                        handleSpeed(rate)
                      }}
                    >
                      {rate}x
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {userData?.id === videoUserId && showReplayGraph && (
            <div className='replay-graph-bar'>
              <ReplayGraphSVG
                data={Array.isArray(segment_data) ? segment_data : []}
                height={75}
              />
            </div>
          )}
        </>
      )}

      {showControls && (
        <div
          className={`videoPlayer__controlsWrapper${
            controlsDisabled
              ? ' videoPlayer__controlsWrapper--disabled'
              : ''
          }`}
          onClick={event => event.stopPropagation()}
        >
          <VideoPlayerControls
            playing={playing}
            setPlaying={value => {
              const next =
                typeof value === 'function'
                  ? value(playing)
                  : value

              if (next) playVideo()
              else pauseVideo()
            }}
            volumeBarShowing={volumeBarShowing}
            muted={muted}
            setMuted={setMuted}
            showReplayGraph={showReplayGraph}
            setShowReplayGraph={setShowReplayGraph}
            isOwner={userData?.id === videoUserId}
            progressData={progressData}
            getVideoElement={getMediaVideoElement}
            handleOnSeekChange={handleOnSeekChange}
            minimized={minimized}
            videoDuration={videoDuration}
            displayDuration={duration}
          />
        </div>
      )}
    </div>
  )
}

export default VideoPlayer